import { ClickAwayListener, Divider, MenuList, useTheme } from '@mui/material'
import ListItemIcon from '@mui/material/ListItemIcon/ListItemIcon'
import ListItemText from '@mui/material/ListItemText/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import { SxProps, Theme } from '@mui/material/styles'
import { EventMenuDelete } from 'assets/icons/event-menu'
import {
  GroupMenuAllCalendars,
  GroupMenuAllCalendarsOn,
  GroupMenuArchive,
  GroupMenuArchiveOff,
  GroupMenuCalendarDetails,
  GroupMenuDefault,
  GroupMenuLeave,
  GroupMenuReminders,
  GroupMenuRemindersOn,
  GroupMenuUnread,
  GroupMenuUnreadOff,
  GroupMenuYouOwn,
  GroupMenuYouOwnOff
} from 'assets/icons/group-menu'
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog'
import { performUpdateGroupSettings } from 'components/popups/groupInfo/SettingsForCalendarSection'
import { useCalendar } from 'components/providers/CalendarProvider'
import { useNotification } from 'components/providers/NotificationProvider'
import { GroupContextMenuProps } from 'components/sidebar/Groups'
import { t } from 'i18next'
import React, { useEffect, useRef, useState } from 'react'
import {
  buildRemoveParticipantData,
  updateGroupOnServer
} from 'services/api/group'
import { localGroupById } from 'services/db/Queries/GroupQueries'
import { isOwnerForGroup } from 'services/db/utils/GroupUtils'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import { theme } from 'theme'
import {
  AccountDoc,
  GroupDoc,
  GroupStatus,
  UserSettingsDoc
} from 'types/api/changes'
import { Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import {
  isGroupBlockedToAddEvents,
  isUserActiveInGroup,
  isUserLastActiveInGroup
} from 'utils/api/groups'
import {
  isGroupArchived,
  isGroupHidden,
  isGroupMuted,
  isGroupShowingEventsYouOwn,
  isGroupUnread
} from 'utils/api/userSettings'
import { isGroupcalApp } from 'utils/appType'
import { APP_COLOR } from 'utils/colors'
import {
  changeGroupStatus,
  deleteGroupForUser,
  exitFromGroup,
  isConvertedGroup,
  toggleArchiveGroup
} from 'utils/groups'

export interface GroupMutationEvent {
  type: 'archive-toggle' | 'group-removed'
  groupId: string
  isArchived?: boolean
}

interface ContextMenuProps {
  group: GroupDoc
  userSettings?: Model<UserSettingsDoc> | UserSettingsDoc
  account?: Model<AccountDoc>
  onMutation?: (event: GroupMutationEvent) => void
  x: number // X-coordinate of the click event
  y: number // Y-coordinate of the click event
  contextMenuPosition: GroupContextMenuProps
  onClose: () => void // Callback to close the context menu
  containerTop: number // Top position of the container relative to viewport
}

export const menuItemStyle: SxProps<Theme> = { padding: '8px 4px' }

export const menuItemTextStyle: SxProps<Theme> = {
  fontSize: '14px',
  fontFamily: 'NotoSans',
  fontWeight: 400
}

export const iconSX: SxProps<Theme> = {
  minWidth: '26px !important'
}

const GroupItemContextMenu: React.FC<ContextMenuProps> = ({
  group,
  userSettings,
  account,
  onMutation,
  x,
  y,
  contextMenuPosition,
  onClose,
  containerTop
}) => {
  const theme = useTheme()
  const menuRef = useRef<HTMLUListElement>(null)
  const [adjustedPosition, setAdjustedPosition] = useState({ top: y, left: x })
  const appNotifications = useNotification()

  useEffect(() => {
    if (!menuRef.current) return

    const menuRect = menuRef.current.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    // Calculate absolute viewport Y position
    const absoluteY = containerTop + y
    const bottomSpace = viewportHeight - absoluteY

    // If menu extends beyond viewport bottom, position it above the click point
    if (bottomSpace < menuRect.height + 10) {
      setAdjustedPosition({
        top: Math.max(10, y - menuRect.height),
        left: x
      })
    } else {
      setAdjustedPosition({ top: y, left: x })
    }
  }, [y, x, containerTop])

  const archiveText = isGroupArchived(userSettings, group._id ?? '')
    ? t('unarchive')
    : t('archive')
  const archiveIcon = isGroupArchived(userSettings, group._id ?? '') ? (
    <GroupMenuArchiveOff />
  ) : (
    <GroupMenuArchive />
  )
  const unreadText = isGroupUnread(userSettings, group._id ?? '')
    ? t('markAsRead')
    : t('markAsUnread')
  const unreadIcon = isGroupUnread(userSettings, group._id ?? '') ? (
    <GroupMenuUnreadOff />
  ) : (
    <GroupMenuUnread />
  )
  const allCalendarsText = isGroupHidden(userSettings, group._id ?? '')
    ? t('showOnAllCal', { cal: t('allCalendarsButton') })
    : t('dontShowOnAllCal', { cal: t('allCalendarsButton') })
  const allCalendarsIcon = isGroupHidden(userSettings, group._id ?? '') ? (
    <GroupMenuAllCalendarsOn />
  ) : (
    <GroupMenuAllCalendars />
  )

  const remindersText = isGroupMuted(userSettings, group._id ?? '')
    ? t('enableNotifications')
    : t('disableNotifications')
  const remindersIcon = isGroupMuted(userSettings, group._id ?? '') ? (
    <GroupMenuRemindersOn />
  ) : (
    <GroupMenuReminders />
  )

  const youOwnText = isGroupShowingEventsYouOwn(userSettings, group._id ?? '')
    ? t('showAllEvents')
    : t('showOnlyYourEvents')
  const youOwnIcon = isGroupShowingEventsYouOwn(
    userSettings,
    group._id ?? ''
  ) ? (
    <GroupMenuYouOwnOff />
  ) : (
    <GroupMenuYouOwn />
  )

  const calendar = useCalendar()

  const showCalendarDetails = () => {
    if (group._id) {
      calendar.onGroupServer(group._id)
      setTimeout(() => {
        calendar.onGroupInfo(group._id)
      }, 50)
    }

    onClose()
  }

  const handleArchiveCalendar = () => {
    // Logic for "Archive calendar" action
    const groupId = group._id ?? ''
    const wasArchived = isGroupArchived(userSettings, groupId)

    onClose()
    toggleArchiveGroup(group, userSettings).then(() => {
      calendar.onGroupServer('')
      if (onMutation) {
        onMutation({
          type: 'archive-toggle',
          groupId,
          isArchived: !wasArchived
        })
      }
    })
  }

  const handleMarkAsUnread = () => {
    // Logic for "Mark as unread" action
    onClose()
    const newValue = isGroupUnread(
      userSettings,
      contextMenuPosition?.group._id ?? ''
    )
      ? '0'
      : '1'
    performUpdateGroupSettings(
      userSettings,
      contextMenuPosition?.group._id ?? '',
      {
        Unread: newValue
      }
    )
  }

  const handleHideFromCalendars = () => {
    // Logic for "Don't show on all calendars" action
    onClose()

    const newValue = isGroupHidden(
      userSettings,
      contextMenuPosition?.group._id ?? ''
    )
      ? '1'
      : '0'
    performUpdateGroupSettings(
      userSettings,
      contextMenuPosition?.group._id ?? '',
      {
        ShowOnAllCalendars: newValue
      }
    )
  }

  const makeDefaultCalendar = () => {
    performUpdateGroupSettings(
      userSettings,
      contextMenuPosition?.group._id ?? '',
      {
        Default: '1'
      }
    ).then(() => {
      onClose()
    })
  }

  function handleReminderState(): void {
    performUpdateGroupSettings(
      userSettings,
      contextMenuPosition?.group._id ?? '',
      {
        ActivateReminders: isGroupMuted(userSettings, group._id ?? '')
          ? '1'
          : '0'
      }
    ).then(() => {
      onClose()
    })
  }

  function handleYouOwnGroup(): void {
    performUpdateGroupSettings(
      userSettings,
      contextMenuPosition?.group._id ?? '',
      {
        ShoWOnlyEventsIOwn: isGroupShowingEventsYouOwn(
          userSettings,
          group._id ?? ''
        )
          ? '0'
          : '1'
      }
    ).then(() => {
      onClose()
    })
  }

  const [showDeleteExplanation, setShowDeleteExplanation] = useState(false)
  const [showLeaveExplanation, setShowLeaveExplanation] = useState(false)
  const [showLeaveAndDeleteConfirmation, setShowLeaveAndDeleteConfirmation] =
    useState(false)

  const isUserActive = isUserActiveInGroup(
    contextMenuPosition.group,
    LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
  )

  const isUserLastActive = isUserLastActiveInGroup(
    contextMenuPosition.group,
    LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
  )

  const shouldHideLeaveActions =
    isConvertedGroup(contextMenuPosition.group) &&
    isOwnerForGroup(contextMenuPosition.group)

  function performGroupExit() {
    const groupId = contextMenuPosition.group._id ?? ''

    localGroupById(contextMenuPosition.group._id ?? '').then((group) => {
      return exitFromGroup(group)
        .then(() => {
          calendar.onGroupServer('')
          if (onMutation) onMutation({ type: 'group-removed', groupId })
          onClose()
        })
        .catch((e) => {
          Logger.red(e)
          appNotifications.showNotification(
            t('oopsError'),
            'error',
            'bottom',
            3000
          )
          onClose()
        })
    })
  }

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      touchEvent="onTouchStart"
      onClickAway={onClose}
    >
      <div>
        {showDeleteExplanation && (
          <ConfirmationDialog
            title={t('areYouSure')}
            content={t('deleteCalendarExplanation')}
            open
            rightButton={t('delete')}
            leftButtonColor="grey"
            handleRightButton={() => {
              // Logic for deleting the calendar
              setShowDeleteExplanation(false)
              localGroupById(contextMenuPosition.group._id ?? '').then(
                (group) => {
                  const groupId = contextMenuPosition.group._id ?? ''
                  return changeGroupStatus(group, GroupStatus.DELETED).then(
                    () => {
                      calendar.onGroupServer('')
                      if (onMutation)
                        onMutation({ type: 'group-removed', groupId })
                      onClose()
                    }
                  )
                }
              )
            }}
            handleLeftButton={() => {
              setShowDeleteExplanation(false)
            }}
          />
        )}
        {showLeaveExplanation && (
          <ConfirmationDialog
            title={t('areYouSure')}
            content={t('leaveCalendarDesc')}
            open
            leftButtonColor="grey"
            rightButton={t('leave')}
            rightButtonColor={APP_COLOR}
            handleRightButton={() => {
              performGroupExit()
            }}
            handleLeftButton={() => {
              setShowLeaveExplanation(false)
            }}
          />
        )}
        {showLeaveAndDeleteConfirmation && (
          <ConfirmationDialog
            title={t('areYouSure')}
            content={t('deleteCalendarDesc')}
            rightButton={t('leaveAnddelete')}
            open
            leftButtonColor="grey"
            rightButtonColor={APP_COLOR}
            handleRightButton={() => {
              localGroupById(contextMenuPosition.group._id ?? '')
                .then((group) => {
                  const groupId = contextMenuPosition.group._id ?? ''
                  return deleteGroupForUser(group).then(() => groupId)
                })
                .then((groupId) => {
                  calendar.onGroupServer('')
                  if (onMutation) onMutation({ type: 'group-removed', groupId })
                  onClose()
                })
                .catch((e) => {
                  Logger.red(e)
                  appNotifications.showNotification(
                    t('oopsError'),
                    'error',
                    'bottom',
                    3000
                  )
                  onClose()
                })
            }}
            handleLeftButton={() => {
              setShowLeaveAndDeleteConfirmation(false)
            }}
          />
        )}
        <ul
          ref={menuRef}
          style={{
            position: 'absolute',
            width: 'fit-content',
            top: adjustedPosition.top,
            left: adjustedPosition.left,
            listStyleType: 'none',
            zIndex: 99999,
            fontSize: '14px',
            padding: '0px 8px',
            borderRadius: '20px',
            backgroundColor: theme.palette.card.primary,
            color: theme.palette.text.primary,
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0 10px 30px rgba(0, 0, 0, 0.35)'
                : '0 2px 12px rgba(0, 0, 0, 0.12)'
          }}
        >
          <MenuList sx={{ fontSize: '14px' }}>
            <MenuItem
              id="show-group-details-button"
              onClick={showCalendarDetails}
              sx={menuItemStyle}
            >
              <ListItemIcon sx={iconSX}>
                <GroupMenuCalendarDetails />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ sx: menuItemTextStyle }}>
                {t('calendarDetails')}
              </ListItemText>
            </MenuItem>
            <Divider
              sx={{ ml: 1, borderColor: theme.palette.border.primary }}
            />
            {isGroupcalApp() && (
              <MenuItem sx={menuItemStyle} onClick={handleMarkAsUnread}>
                <ListItemIcon sx={iconSX}>{unreadIcon}</ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ sx: menuItemTextStyle }}
                >
                  {unreadText}
                </ListItemText>
              </MenuItem>
            )}
            {
              <MenuItem
                id="show-on-all-calendar-button"
                sx={menuItemStyle}
                onClick={handleHideFromCalendars}
              >
                <ListItemIcon sx={iconSX}>{allCalendarsIcon}</ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ sx: menuItemTextStyle }}
                >
                  {allCalendarsText}
                </ListItemText>
              </MenuItem>
            }

            {
              <MenuItem sx={menuItemStyle} onClick={handleReminderState}>
                <ListItemIcon sx={iconSX}>{remindersIcon}</ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ sx: menuItemTextStyle }}
                >
                  {remindersText}
                </ListItemText>
              </MenuItem>
            }

            {account &&
              !isGroupBlockedToAddEvents(
                account,
                group,
                userSettings,
                LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
              ) && (
                <MenuItem
                  id="make-default-calendar-button"
                  sx={menuItemStyle}
                  onClick={makeDefaultCalendar}
                >
                  <ListItemIcon sx={iconSX}>
                    <GroupMenuDefault />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{ sx: menuItemTextStyle }}
                  >
                    {t('defaultCalendar')}
                  </ListItemText>
                </MenuItem>
              )}
            {isGroupcalApp() && (
              <MenuItem
                id="you-own-group-button"
                sx={menuItemStyle}
                onClick={handleYouOwnGroup}
              >
                <ListItemIcon sx={iconSX}>{youOwnIcon}</ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ sx: menuItemTextStyle }}
                >
                  {youOwnText}
                </ListItemText>
              </MenuItem>
            )}
            <Divider
              sx={{ ml: 1, borderColor: theme.palette.border.primary }}
            />
            {isGroupcalApp() && (
              <MenuItem
                id="archive-group-button"
                sx={menuItemStyle}
                onClick={handleArchiveCalendar}
              >
                <ListItemIcon sx={iconSX}>{archiveIcon}</ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ sx: menuItemTextStyle }}
                >
                  {archiveText}
                </ListItemText>
              </MenuItem>
            )}
            {isGroupcalApp() &&
              isUserActive &&
              !isUserLastActive &&
              !shouldHideLeaveActions && (
                <MenuItem
                  id="leave-group-button"
                  sx={menuItemStyle}
                  onClick={() => {
                    // onClose()
                    setShowLeaveExplanation(true)
                  }}
                >
                  <ListItemIcon sx={iconSX}>
                    <GroupMenuLeave />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{
                      sx: menuItemTextStyle,
                      color: 'var(--color-negative)'
                    }}
                  >
                    {t('leave')}
                  </ListItemText>
                </MenuItem>
              )}
            {isGroupcalApp() && isUserActive && !shouldHideLeaveActions && (
              <MenuItem
                id="leave-and-delete-group-button"
                sx={menuItemStyle}
                onClick={() => {
                  // onClose()
                  setShowLeaveAndDeleteConfirmation(true)
                }}
              >
                <ListItemIcon sx={iconSX}>
                  <EventMenuDelete />
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{
                    sx: menuItemTextStyle,
                    color: 'var(--color-negative)'
                  }}
                >
                  {t('leaveAnddelete')}
                </ListItemText>
              </MenuItem>
            )}
            {isGroupcalApp() && !isUserActive && (
              <MenuItem
                id="delete-group-button"
                sx={menuItemStyle}
                onClick={() => {
                  setShowDeleteExplanation(true)
                }}
              >
                <ListItemIcon sx={iconSX}>
                  <EventMenuDelete />
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{
                    sx: menuItemTextStyle,
                    color: 'var(--color-negative)'
                  }}
                >
                  {t('delete')}
                </ListItemText>
              </MenuItem>
            )}
          </MenuList>
        </ul>
      </div>
    </ClickAwayListener>
  )
}

export default GroupItemContextMenu
